import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Building2, Phone, Mail, Globe, FileText, Printer, Download, CheckCircle2, XCircle, Clock, ShieldCheck, X } from "lucide-react";
import Barcode from "react-barcode";
import { QRCodeSVG } from "qrcode.react";
import { toast, Toaster } from "sonner";
import PrintSheet from "./PrintSheet";
import { currencyLabel, currencySymbol, snapshotRates } from "./studioUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n, c = "₺") =>
  `${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}`;

const STATUS_COLOR = {
  taslak: "#94a3b8", gonderildi: "#3b82f6", gorusuluyor: "#f59e0b",
  onaylandi: "#059669", reddedildi: "#dc2626", beklemede: "#f59e0b",
};
const STATUS_LABEL = {
  taslak: "Taslak", gonderildi: "Gönderildi", gorusuluyor: "Görüşülüyor",
  onaylandi: "Onaylandı", reddedildi: "Reddedildi", beklemede: "Beklemede",
};
const DECISIONS = [
  { value: "onaylandi", label: "Onayla", icon: CheckCircle2, color: "#059669" },
  { value: "reddedildi", label: "Reddet", icon: XCircle, color: "#dc2626" },
  { value: "beklemede", label: "Beklemeye al", icon: Clock, color: "#f59e0b" },
];

export default function PublicQuote() {
  const { ref } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [printMode, setPrintMode] = useState("simple"); // simple | full
  const [displayCurrency, setDisplayCurrency] = useState("TRY");
  const [largeImage, setLargeImage] = useState("");
  const [largeQr, setLargeQr] = useState(false);
  const publicUrl = window.location.href;

  useEffect(() => {
    const primary = localStorage.getItem("app-primary-color");
    if (primary) document.documentElement.style.setProperty("--blue", primary);
    axios.get(`${API}/public/quotes/${ref}`)
      .then(res => {
        setData(res.data);
        if (res.data.company?.primary_color) {
          document.documentElement.style.setProperty("--blue", res.data.company.primary_color);
        }
      })
      .catch(() => setError("Teklif bulunamadı veya artık geçerli değil."));
  }, [ref]);

  useEffect(() => {
    document.body.classList.toggle("print-full", printMode === "full");
    return () => document.body.classList.remove("print-full");
  }, [printMode]);

  const handlePrint = () => { setPrintMode("simple"); setTimeout(() => window.print(), 60); };
  const handleDownloadPdf = () => {
    setPrintMode("full");
    setTimeout(() => { window.print(); toast.info("Yazdırma penceresinde 'PDF olarak kaydet' seçin"); }, 100);
  };

  const totals = useMemo(() => {
    if (!data) return { usd: 0, tl: 0 };
    return { usd: data.quote.subtotal_usd || 0, tl: data.quote.subtotal_tl || 0 };
  }, [data]);

  if (error) {
    return (
      <div className="public-wrap">
        <div className="public-error" data-testid="public-quote-error">
          <FileText size={40} />
          <h1>Teklif bulunamadı</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="public-wrap"><div className="public-loading">Yükleniyor...</div></div>;

  const { quote, company } = data;
  const label = currencyLabel(quote.currency);
  const symbol = currencySymbol(quote.currency);
  const rates = snapshotRates(quote);
  const approved = quote.customer_approval && quote.customer_approval.confirmed_at;

  return (
    <>
      <div className="public-wrap no-print" data-testid="public-quote-view">
        <div className="public-toolbar">
          <div>
            <b>{company.company_name || "CDS Teknoloji"}</b>
            <span>Ref: {quote.ref_code}</span>
          </div>
          <div className="public-toolbar-actions">
            <button data-testid="public-print-button" className="outline-button" onClick={handlePrint}>
              <Printer size={15} /> Yazdır
            </button>
            <button data-testid="public-download-button" className="primary-button" onClick={handleDownloadPdf}>
              <Download size={15} /> PDF İndir
            </button>
          </div>
        </div>

        <div className="public-sheet">
          <header className="public-head">
            <div className="public-head-left">
              {company.logo_url && <img src={company.logo_url} alt="logo" className="public-logo" />}
              <div>
                <h1 data-testid="public-company-name">{company.company_name || "CDS Teknoloji"}</h1>
                <div className="public-contact">
                  {company.address && <span><Building2 size={13} />{company.address}</span>}
                  {company.phone && <span><Phone size={13} />{company.phone}</span>}
                  {company.email && <span><Mail size={13} />{company.email}</span>}
                  {company.website && <span><Globe size={13} />{company.website}</span>}
                </div>
              </div>
            </div>
            <div className="public-meta">
              <span className="public-badge">PROFORMA TEKLİF</span>
              <p><b>Ref:</b> <span data-testid="public-ref-code">{quote.ref_code}</span></p>
              <p><b>Tarih:</b> {new Date(quote.created_at).toLocaleDateString("tr-TR")}</p>
              <p><b>Geçerlilik:</b> {quote.validity_days || 1} gün</p>
              {quote.status && (
                <p><span className="status-chip" style={{ background: STATUS_COLOR[quote.status] || "#94a3b8" }}>
                  {STATUS_LABEL[quote.status] || quote.status}
                </span></p>
              )}
              <div className="public-codes">
                {quote.ean13 && <div className="public-barcode"><Barcode value={quote.ean13} format="EAN13" width={1.4} height={40} fontSize={10} displayValue={true} margin={0} /></div>}
                <button type="button" className="public-qr" title="QR kodunu büyüt" aria-label="Teklif QR kodunu büyüt" onClick={() => setLargeQr(true)}><QRCodeSVG value={publicUrl} size={90} level="M" marginSize={4} /></button>
              </div>
            </div>
          </header>
          <section className="public-customer">
            <div><b>Sayın</b><span>{quote.customer_name || "-"}</span></div>
            <div><b>Konu</b><span>{quote.subject || "-"}</span></div>
            <div><b>Uygulanan Kur</b><span data-testid="public-applied-rate">{Number(quote.currency_rate).toFixed(2)} ₺ / {label}</span></div>
          </section>
          <div className="public-table-wrap">
            <table className="public-table">
              <thead>
                <tr>
                  {quote.show_images && <th style={{ width: 62 }}>Görsel</th>}
                  <th>Ürün / Hizmet</th>
                  <th>Miktar</th>
                  <th data-testid="public-unit-currency">Birim {label}</th>
                  <th>İskonto</th>
                  <th>Tutar {label}</th>
                  <th>Tutar ₺</th>
                </tr>
              </thead>
              <tbody>
                {quote.items.map((item, i) => (
                  <tr key={i}>
                    {quote.show_images && (
                      <td className="public-thumb">{item.image_url && <button type="button" aria-label={`${item.product_name} görselini büyüt`} onClick={() => setLargeImage(item.image_url)}><img src={item.image_url} alt={item.product_name} /></button>}</td>
                    )}
                    <td>{item.product_name}{item.sku && <small style={{ display: "block", color: "#64748b" }}>SKU: {item.sku}</small>}</td>
                    <td>{item.quantity}</td>
                    <td data-testid={`public-item-price-${i}`}>{money(item.list_price, symbol)}</td>
                    <td>%{item.discount || 0}</td>
                    <td>{money(item.total_usd, symbol)}</td>
                    <td>{money(item.total_tl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <section className="public-summary">
            <div className="public-terms">
              <p><b>Teslimat:</b> {quote.delivery_terms || "-"}</p>
              <p><b>Ödeme:</b> {quote.payment_terms || "-"}</p>
              {quote.notes && <p className="public-note"><em>{quote.notes}</em></p>}
            </div>
            <div className="public-totals">
              {(
                <div className="public-currency-picker" data-testid="public-currency-picker">
                  <span>Görüntüleme:</span>
                  <button className={displayCurrency === "TRY" ? "active" : ""}
                    onClick={() => setDisplayCurrency("TRY")} data-testid="pc-try">₺ TL</button>
                  {rates.USD > 0 && <button className={displayCurrency === "USD" ? "active" : ""}
                    onClick={() => setDisplayCurrency("USD")} data-testid="pc-usd">$ USD</button>}
                  {quote.eur_rate ? (
                    <button className={displayCurrency === "EUR" ? "active" : ""}
                      onClick={() => setDisplayCurrency("EUR")} data-testid="pc-eur">€ EUR</button>
                  ) : null}
                  {quote.gbp_rate ? (
                    <button className={displayCurrency === "GBP" ? "active" : ""}
                      onClick={() => setDisplayCurrency("GBP")} data-testid="pc-gbp">£ GBP</button>
                  ) : null}
                </div>
              )}
              {(() => {
                // Convert TL amount into displayCurrency using stored snapshot rates
                const usdR = rates.USD;
                const eurR = Number(quote.eur_rate || 0);
                const gbpR = Number(quote.gbp_rate || 0);
                const convert = (tl) => {
                  if (displayCurrency === "TRY") return money(tl, "₺");
                  if (displayCurrency === "USD" && usdR) return money(tl / usdR, "$");
                  if (displayCurrency === "EUR" && eurR) return money(tl / eurR, "€");
                  if (displayCurrency === "GBP" && gbpR) return money(tl / gbpR, "£");
                  return money(tl, "₺");
                };
                return (
                  <>
                    {quote.currency !== "TRY" && <p><span>Ara toplam {label}</span><b>{money(quote.subtotal_usd, symbol)}</b></p>}
                    <p><span>Ara toplam ({currencyLabel(displayCurrency)})</span><b data-testid="public-subtotal">{convert(quote.subtotal_tl)}</b></p>
                    <p><span>KDV %{quote.vat_rate}</span><b>{convert(quote.vat_tl)}</b></p>
                    <p className="public-grand"><span>GENEL TOPLAM</span>
                      <b data-testid="public-grand-total">{convert(quote.grand_total)}</b></p>
                  </>
                );
              })()}
            </div>
          </section>
          {company.bank_details && (
            <footer className="public-bank"><b>Banka Bilgileri</b><span>{company.bank_details}</span></footer>
          )}

          {/* Payment CTA if quote has a payment link */}
          {quote.payment_link && (
            <section className="pay-band" data-testid="public-pay-band">
              <div>
                <b>Ödeme yapmak için hazır mısınız?</b>
                <p>Güvenli ödeme sayfasına yönlendirileceksiniz. Ödeme tamamlandığında bize otomatik bildirim gelir.</p>
              </div>
              <a href={quote.payment_link} target="_blank" rel="noopener noreferrer"
                 className="pay-cta-button" data-testid="public-pay-link">
                Ödemeye Devam Et →
              </a>
            </section>
          )}

          {/* Approval band */}
          <section className="approval-band" data-testid="approval-band">
            {approved ? (
              <div className={`approval-done approval-${quote.customer_approval.decision}`}>
                <ShieldCheck size={28} />
                <div>
                  <h3>Teklif {STATUS_LABEL[quote.customer_approval.decision]}</h3>
                  <p>{quote.customer_approval.name} · {quote.customer_approval.email} tarafından
                    {" "}{new Date(quote.customer_approval.confirmed_at).toLocaleString("tr-TR")} tarihinde onaylandı.</p>
                  {quote.customer_approval.note && <p className="approval-note">"{quote.customer_approval.note}"</p>}
                  {quote.customer_approval.certificate_id && (
                    <p className="approval-cert">
                      <b>Onay Sertifikası:</b> <code>{quote.customer_approval.certificate_id}</code>
                      <small>Hash: {quote.customer_approval.certificate_hash?.slice(0, 24)}…</small>
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="approval-intro">
                  <h3>Kararınızı bildirin</h3>
                  <p>Onayınız e-posta doğrulaması ile güvence altına alınır. Yanlışlıkla onay olmaz — kod girmeden karar kaydedilmez.</p>
                </div>
                <div className="approval-buttons">
                  {DECISIONS.map(d => (
                    <button key={d.value} data-testid={`approve-${d.value}`}
                      className="approval-btn"
                      style={{ borderColor: d.color, color: d.color }}
                      onClick={() => setApproveOpen(d.value)}>
                      <d.icon size={18} /> {d.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </section>

          {((company.references && company.references.length > 0) || (company.partners && company.partners.length > 0)) && (
            <section className="public-refs-section">
              {company.references && company.references.length > 0 && (
                <div>
                  <h3>Referanslarımız</h3>
                  <div className="public-refs-grid">
                    {company.references.map((r, i) => (
                      <div className="public-ref-card" key={i}>
                        {r.logo_url && <img src={r.logo_url} alt={r.name} />}
                        <b>{r.name}</b>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {company.partners && company.partners.length > 0 && (
                <div>
                  <h3>Çözüm Ortaklarımız</h3>
                  <div className="public-refs-grid">
                    {company.partners.map((r, i) => (
                      <div className="public-ref-card" key={i}>
                        {r.logo_url && <img src={r.logo_url} alt={r.name} />}
                        <b>{r.name}</b>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          <div className="public-foot">Bu teklif dijital olarak {company.company_name || "firmamız"} tarafından hazırlanmıştır.</div>
        </div>
      </div>

      {approveOpen && (
        <ApprovalModal
          initialDecision={approveOpen}
          refCode={quote.ref_code}
          defaultEmail={quote.customer_email || ""}
          defaultName={quote.customer_contact || quote.customer_name || ""}
          onClose={() => setApproveOpen(false)}
          onDone={(approval) => {
            setData(d => ({ ...d, quote: { ...d.quote, status: approval.decision, customer_approval: { ...approval, confirmed: true } } }));
            setApproveOpen(false);
          }}
        />
      )}

      {largeImage && <div className="public-image-overlay" role="button" tabIndex={0} aria-label="Büyük görseli kapat" onClick={() => setLargeImage("")} onKeyDown={e => { if (e.key === "Escape" || e.key === "Enter") setLargeImage(""); }}><img src={largeImage} alt="Ürün görseli" /></div>}
      {largeQr && <div className="public-image-overlay" role="button" tabIndex={0} aria-label="QR kodunu kapat" onClick={() => setLargeQr(false)} onKeyDown={e => { if (e.key === "Escape" || e.key === "Enter") setLargeQr(false); }}><div className="public-qr-enlarged"><QRCodeSVG value={publicUrl} size={300} level="M" marginSize={4} /><span>Telefon kamerasıyla okutun · Kapatmak için tıklayın</span></div></div>}

      <PrintSheet
        quote={quote} company={company} rate={quote.currency_rate}
        totals={totals} vat={quote.vat_tl}
        refCode={quote.ref_code} createdAt={quote.created_at}
        publicUrl={publicUrl} ean13={quote.ean13}
        showImages={quote.show_images}
        showCustomerSign={printMode === "simple"}
        showSecondPage={printMode === "full"}
      />
      <Toaster position="bottom-right" />
    </>
  );
}

function ApprovalModal({ initialDecision, refCode, defaultEmail, defaultName, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    decision: initialDecision,
    name: defaultName || "",
    email: defaultEmail || "",
    phone: "",
    note: "",
    delivery_channel: "email",
  });
  const [requestId, setRequestId] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const decision = DECISIONS.find(d => d.value === form.decision);

  const requestCode = async () => {
    if (!form.name.trim()) return toast.error("Ad soyad zorunlu");
    if (form.delivery_channel === "email" && !form.email.trim()) return toast.error("E-posta adresi gerekli");
    if (form.delivery_channel === "sms" && !form.phone.trim()) return toast.error("Cep telefonu gerekli");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/quotes/${refCode}/approval-request`, form);
      setRequestId(data.request_id);
      setStep(2);
      const channels = [data.email_sent && "e-posta", data.sms_sent && "SMS"].filter(Boolean).join(" ve ");
      toast.success(`Doğrulama kodu ${channels} ile gönderildi`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız");
    } finally { setBusy(false); }
  };
  const confirmCode = async () => {
    if (code.length < 6) return toast.error("6 haneli kodu girin");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/quotes/${refCode}/approval-confirm`,
        { request_id: requestId, code });
      toast.success("Kararınız kaydedildi");
      onDone({ ...form, decision: data.decision, confirmed_at: data.confirmed_at });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kod hatalı");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card lg" onClick={e => e.stopPropagation()} data-testid="approval-modal">
        <button className="modal-close" onClick={onClose}><X size={16} /></button>
        <div className="approval-modal-head" style={{ color: decision.color }}>
          <decision.icon size={26} />
          <h2>Teklifi {decision.label}</h2>
        </div>
        {step === 1 ? (
          <>
            <p>6 haneli, <b>15 dakika geçerli</b> doğrulama kodunu hangi yolla almak istediğinizi seçin. Kodu doğrulamadan kararınız kaydedilmez.</p>
            <div role="radiogroup" aria-label="Doğrulama kodu gönderim yöntemi" style={{ display: "flex", gap: 16, marginBottom: 16 }}>
              <label><input type="radio" name="approval-channel" value="email" checked={form.delivery_channel === "email"} onChange={() => setForm({ ...form, delivery_channel: "email" })} /> E-posta</label>
              <label><input type="radio" name="approval-channel" value="sms" checked={form.delivery_channel === "sms"} onChange={() => setForm({ ...form, delivery_channel: "sms" })} /> SMS</label>
            </div>
            <div className="settings-grid">
              <label>Ad Soyad<input data-testid="approval-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} autoFocus /></label>
              {form.delivery_channel === "email" ?
                <label>E-posta<input data-testid="approval-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></label> :
                <label>Cep telefonu<input data-testid="approval-phone" type="tel" inputMode="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="05xx xxx xx xx" /></label>}
              <label>Kararınıza ekleyeceğiniz not (opsiyonel)<input data-testid="approval-note" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Ek talep / açıklama" /></label>
            </div>
            <div className="settings-actions">
              <button className="outline-button" onClick={onClose}>Vazgeç</button>
              <button className="primary-button" onClick={requestCode} disabled={busy} data-testid="approval-send-code"
                style={{ background: decision.color, boxShadow: `0 5px 12px ${decision.color}30` }}>
                {busy ? "Gönderiliyor..." : "Doğrulama kodu gönder"}
              </button>
            </div>
          </>
        ) : (
          <>
            <p>Size gönderilen <b>6 haneli kodu</b> girin. Bu adım olmadan kararınız kaydedilmez.</p>
            <input data-testid="approval-code" type="text" inputMode="numeric" maxLength={6} pattern="[0-9]*"
              value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
              className="picker-search approval-code-input" placeholder="000000" autoFocus />
            <div className="settings-actions">
              <button className="outline-button" onClick={() => setStep(1)}>Geri</button>
              <button className="primary-button" onClick={confirmCode} disabled={busy || code.length < 6}
                data-testid="approval-confirm"
                style={{ background: decision.color, boxShadow: `0 5px 12px ${decision.color}30` }}>
                {busy ? "Kaydediliyor..." : "Kararımı onayla"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
